import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Course } from '../data/courses';
import { getExitTicket, type ExitTicketQuestion } from '../data/exit-tickets';
import type { ActivitySummary, Subject } from '../types/activity';
import type { AgeGroup } from '../types/child';
import {
  deriveFlashcardsFromCourse,
  LEITNER_INTERVALS,
  REVIEW_CAPS,
  scheduleNextReview,
  selectDueCards,
  type Flashcard,
  type ReviewResult,
  type ReviewState,
} from '../engine/spaced-repetition';
import { todayStr } from '../utils/date';

export type ReviewSourceType = 'course-lesson' | 'activity-exit-ticket' | 'quiz-miss';
export type ReviewableActivity = Pick<ActivitySummary, 'id' | 'title' | 'subject' | 'futureSkills'>;

/** Minimal shape shared by Science/History/Finance quiz questions — enough to
 *  build a review card from a missed question. */
export interface MissableQuestion {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
  category?: string;
  explanation?: string;
}

export interface StoredReviewCard extends Flashcard {
  childId: string;
  sourceType: ReviewSourceType;
  subject: string;
  courseId?: string;
  courseTitle?: string;
  lessonId?: string;
  lessonTitle?: string;
  activityId?: string;
  activityTitle?: string;
  activitySubject?: Subject;
  activityQuestionIndex?: number;
  tiedObjective?: string;
  createdAt: string;
}

export interface HydratedReviewCard {
  card: StoredReviewCard;
  state: ReviewState;
}

interface ReviewStreakState {
  count: number;
  lastDate: string;
}

interface ReviewStateStore {
  cards: Record<string, StoredReviewCard>;
  reviewStates: Record<string, ReviewState>;
  reviewStreaks: Record<string, ReviewStreakState>;

  seedCourseLesson: (
    childId: string,
    course: Course,
    lessonId: string,
    ageGroup: AgeGroup,
    completedOn?: string,
  ) => number;
  seedActivityExitTicket: (
    childId: string,
    activity: ReviewableActivity,
    ageGroup: AgeGroup,
    completedOn?: string,
  ) => number;
  /** Enqueue a missed quiz question for spaced re-practice. Idempotent by
   *  question — a repeat miss just (re)schedules the card to Box 1, due soon,
   *  so it resurfaces in the Daily Review Quest. Returns 1. */
  seedQuizMiss: (
    childId: string,
    question: MissableQuestion,
    subject: string,
    ageGroup: AgeGroup,
    missedOn?: string,
  ) => number;
  answerReview: (childId: string, cardId: string, correct: boolean, reviewedOn?: string) => void;
  getDueReviews: (childId: string, ageGroup: AgeGroup, date?: string, limit?: number) => HydratedReviewCard[];
  getStats: (childId: string, date?: string) => ReviewStats;
  /** Records that the child finished a review session today; returns the new
   *  review-streak count. Idempotent within a day. */
  recordReviewSession: (childId: string, date?: string) => number;
  getReviewStreak: (childId: string) => number;
}

export interface ReviewStats {
  total: number;
  due: number;
  graduated: number;
  reviewedToday: number;
  nextDueDate: string | null;
}

function storeKey(childId: string, cardId: string): string {
  return `${childId}::${cardId}`;
}

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function initialScheduledState(cardId: string, completedOn: string): ReviewState {
  return {
    cardId,
    box: 1,
    nextReviewDate: addDays(completedOn, LEITNER_INTERVALS[1]),
    lastReviewDate: '',
    reviewCount: 0,
    graduated: false,
  };
}

export function buildCourseLessonReviewCards(
  childId: string,
  course: Course,
  lessonId: string,
  ageGroup: AgeGroup,
  createdAt: string,
): StoredReviewCard[] {
  const lesson = course.lessons.find((candidate) => candidate.id === lessonId);
  if (!lesson) return [];

  return deriveFlashcardsFromCourse(course)
    .filter((card) =>
      card.ageGroup === ageGroup
      && card.source.kind === 'course-fact'
      && card.source.lessonId === lessonId
    )
    .map((card) => ({
      ...card,
      childId,
      sourceType: 'course-lesson' as const,
      subject: course.subject,
      courseId: course.id,
      courseTitle: course.title,
      lessonId,
      lessonTitle: lesson.title,
      createdAt,
    }));
}

function answerForExitTicketQuestion(question: ExitTicketQuestion): string {
  if (
    question.type === 'multiple-choice'
    && typeof question.correctIndex === 'number'
    && question.options?.[question.correctIndex]
  ) {
    return question.options[question.correctIndex];
  }

  return question.parentCheckRubric ?? question.tiedObjective;
}

export function buildActivityExitTicketReviewCards(
  childId: string,
  activity: ReviewableActivity,
  ageGroup: AgeGroup,
  createdAt: string,
): StoredReviewCard[] {
  const ticket = getExitTicket(activity.id, ageGroup);
  if (!ticket) return [];

  return ticket.questions.map((question, questionIndex) => ({
    id: `activity:${activity.id}:${ageGroup}:exit-ticket:${questionIndex}`,
    prompt: question.prompt,
    answer: answerForExitTicketQuestion(question),
    options: question.options,
    ageGroup,
    tags: [
      activity.subject,
      question.type,
      question.tiedObjective,
      ...(activity.futureSkills ?? []),
    ],
    source: {
      kind: 'activity-exit-ticket' as const,
      activityId: activity.id,
      tier: ageGroup,
      questionIndex,
      tiedObjective: question.tiedObjective,
    },
    childId,
    sourceType: 'activity-exit-ticket' as const,
    subject: activity.subject,
    activityId: activity.id,
    activityTitle: activity.title,
    activitySubject: activity.subject,
    activityQuestionIndex: questionIndex,
    tiedObjective: question.tiedObjective,
    createdAt,
  }));
}

/** Card id is STABLE per (subject, question) so a repeat miss reschedules the
 *  same card rather than spawning duplicates. */
export function quizMissCardId(subject: string, questionId: string): string {
  return `quiz-miss:${subject}:${questionId}`;
}

export function buildQuizMissReviewCard(
  childId: string,
  question: MissableQuestion,
  subject: string,
  ageGroup: AgeGroup,
  createdAt: string,
): StoredReviewCard {
  const correct = question.options[question.correctIndex] ?? '';
  // Carry the explanation onto the answer reveal so the re-practice card also
  // re-teaches the "why", not just the right option.
  const answer = question.explanation ? `${correct} — ${question.explanation}` : correct;
  return {
    id: quizMissCardId(subject, question.id),
    prompt: question.question,
    answer,
    options: question.options,
    ageGroup,
    tags: [subject, 'quiz-miss', ...(question.category ? [question.category] : [])],
    source: {
      kind: 'quiz-miss',
      questionId: question.id,
      subject,
      ...(question.category ? { category: question.category } : {}),
    },
    childId,
    sourceType: 'quiz-miss',
    subject,
    activityTitle: 'Review Quest reinforcement',
    createdAt,
  };
}

// Inlined to avoid importing faith-course-ids.ts, which rollup colocates with
// chunk-courses-data (4 MB) — creating a static entry→courses-data edge that
// forces every visitor to download the full course catalog before first paint.
// Canonical source: src/data/faith-course-ids.ts (validated by its test).
const FAITH_COURSE_IDS = new Set(['bible-foundations']);

export function selectDueReviewCards(
  cards: Record<string, StoredReviewCard>,
  reviewStates: Record<string, ReviewState>,
  childId: string,
  ageGroup: AgeGroup,
  date: string = todayStr(),
  limit: number = REVIEW_CAPS[ageGroup].soft,
  opts?: { includeFaithContent?: boolean },
): HydratedReviewCard[] {
  // Fail-safe faith gating: cards seeded from faith-based courses (e.g. Bible
  // Foundations) are EXCLUDED unless the caller explicitly passes the child's
  // faithContentOptIn. A parent turning faith content off must retroactively
  // silence already-seeded review cards ("nothing faith-based surfaces
  // anywhere for this child" — src/types/family.ts), and a forgetful new call
  // site should hide rather than leak.
  const includeFaith = opts?.includeFaithContent === true;
  const childCards = Object.values(cards).filter(
    (card) =>
      card.childId === childId &&
      (includeFaith || !card.courseId || !FAITH_COURSE_IDS.has(card.courseId)),
  );
  const childStates: Record<string, ReviewState> = {};
  for (const card of childCards) {
    const state = reviewStates[storeKey(childId, card.id)];
    if (state) childStates[card.id] = state;
  }

  const selected = selectDueCards(childCards, childStates, limit, date);
  return selected
    .map((card) => {
      const state = childStates[card.id];
      return state ? { card, state } : null;
    })
    .filter((item): item is HydratedReviewCard => item !== null);
}

export function getReviewStats(
  cards: Record<string, StoredReviewCard>,
  reviewStates: Record<string, ReviewState>,
  childId: string,
  date: string = todayStr(),
): ReviewStats {
  const childCards = Object.values(cards).filter((card) => card.childId === childId);
  const states = childCards
    .map((card) => reviewStates[storeKey(childId, card.id)])
    .filter((state): state is ReviewState => Boolean(state));

  let due = 0;
  let graduated = 0;
  let reviewedToday = 0;
  let nextDueDate: string | null = null;

  for (const state of states) {
    if (state.graduated) graduated += 1;
    if (state.lastReviewDate === date) reviewedToday += 1;
    if (!state.graduated && state.nextReviewDate <= date) due += 1;
    if (!state.graduated && state.nextReviewDate > date) {
      if (nextDueDate === null || state.nextReviewDate.localeCompare(nextDueDate) < 0) {
        nextDueDate = state.nextReviewDate;
      }
    }
  }

  return {
    total: childCards.length,
    due,
    graduated,
    reviewedToday,
    nextDueDate,
  };
}

export const useReviewStore = create<ReviewStateStore>()(
  persist(
    (set, get) => ({
      cards: {},
      reviewStates: {},
      reviewStreaks: {},

      seedCourseLesson: (childId, course, lessonId, ageGroup, completedOn = todayStr()) => {
        const cards = buildCourseLessonReviewCards(childId, course, lessonId, ageGroup, new Date().toISOString());
        if (cards.length === 0) return 0;

        let added = 0;
        set((state) => {
          const nextCards = { ...state.cards };
          const nextReviewStates = { ...state.reviewStates };

          for (const card of cards) {
            const key = storeKey(childId, card.id);
            if (!nextCards[key]) {
              nextCards[key] = card;
            }
            if (!nextReviewStates[key]) {
              nextReviewStates[key] = initialScheduledState(card.id, completedOn);
              added += 1;
            }
          }

          return { cards: nextCards, reviewStates: nextReviewStates };
        });

        return added;
      },

      seedActivityExitTicket: (childId, activity, ageGroup, completedOn = todayStr()) => {
        const cards = buildActivityExitTicketReviewCards(childId, activity, ageGroup, new Date().toISOString());
        if (cards.length === 0) return 0;

        let added = 0;
        set((state) => {
          const nextCards = { ...state.cards };
          const nextReviewStates = { ...state.reviewStates };

          for (const card of cards) {
            const key = storeKey(childId, card.id);
            if (!nextCards[key]) {
              nextCards[key] = card;
            }
            if (!nextReviewStates[key]) {
              nextReviewStates[key] = initialScheduledState(card.id, completedOn);
              added += 1;
            }
          }

          return { cards: nextCards, reviewStates: nextReviewStates };
        });

        return added;
      },

      seedQuizMiss: (childId, question, subject, ageGroup, missedOn = todayStr()) => {
        if (!childId || !question?.id) return 0;
        const card = buildQuizMissReviewCard(childId, question, subject, ageGroup, new Date().toISOString());
        const key = storeKey(childId, card.id);

        set((state) => ({
          // Preserve the card content if it already exists; always (re)schedule
          // to Box 1 due tomorrow — a miss means "bring this back soon".
          cards: { ...state.cards, [key]: state.cards[key] ?? card },
          reviewStates: { ...state.reviewStates, [key]: initialScheduledState(card.id, missedOn) },
        }));

        return 1;
      },

      answerReview: (childId, cardId, correct, reviewedOn = todayStr()) => {
        const key = storeKey(childId, cardId);
        const existing = get().reviewStates[key];
        if (!existing) return;

        const result: ReviewResult = {
          cardId,
          correct,
          reviewedOn,
        };

        set((state) => ({
          reviewStates: {
            ...state.reviewStates,
            [key]: scheduleNextReview(existing, result),
          },
        }));
      },

      getDueReviews: (childId, ageGroup, date = todayStr(), limit = REVIEW_CAPS[ageGroup].soft) =>
        selectDueReviewCards(get().cards, get().reviewStates, childId, ageGroup, date, limit),

      getStats: (childId, date = todayStr()) =>
        getReviewStats(get().cards, get().reviewStates, childId, date),

      recordReviewSession: (childId, date = todayStr()) => {
        const prev = get().reviewStreaks[childId];
        let count = 1;
        if (prev) {
          if (prev.lastDate === date) return prev.count; // already counted today
          count = prev.lastDate === addDays(date, -1) ? prev.count + 1 : 1;
        }
        set((state) => ({
          reviewStreaks: { ...state.reviewStreaks, [childId]: { count, lastDate: date } },
        }));
        return count;
      },

      getReviewStreak: (childId) => get().reviewStreaks[childId]?.count ?? 0,
    }),
    {
      name: 'homeschool-reviews',
      version: 1,
      migrate: (state) => {
        const s = (state ?? {}) as Partial<ReviewStateStore>;
        if (!s.reviewStreaks) s.reviewStreaks = {};
        return s as ReviewStateStore;
      },
    },
  ),
);
